import React from "react";
import { AuroraBackground } from "../../components/ui/aurora-background";

const profiles = [
  {
    name: "Kaspar Rothenfusser",
    role: "Product",
    linkedin: "https://www.linkedin.com/in/k-rothenfusser/",
    image: "/kaspar.jpeg",
    bio: `Polymath, researcher, and serial entrepreneur. During his interdisciplinary studies, he developed a bold, highly scientific approach to complex problems. Kaspar’s research is the origin of our work and he brings expertise in ontology, computer science, Artificial Intelligence, and Complex Collaborative Systems to the company which are crucial to solving the challenge at hand.`,
  },
  {
    name: "Bekk Blando",
    role: "Tech",
    linkedin: "https://www.linkedin.com/in/bekk-blando-a3b97aa9/",
    image: "/bekk.jpeg",
    bio: `A problem solver with 9 years of full-stack experience. While doing a bachelor's in math Bekk was a visiting researcher at Stanford focusing on Massive Computational Experiments. In addition, Bekk has been a CTO to many startups in the AI space and brings Machine learning, Search Systems, and Natural Language Processing expertise to our company `,
  },
  {
    name: "Rens Dommerholt",
    role: "Company",
    linkedin: "https://www.linkedin.com/in/rensdommerholt/",
    image: "/rens.jpeg",
    bio: `Experienced executive and manager. Successfully initiated, completed, and acquired funding for 4 products with a total budget of €2M in the last 4 years. Former incubator manager of the most entrepreneurial Dutch University that brings exceptional execution, speed, and insights from 250 startups to our company. Including a strong, ready-to-call network in the Netherlands.`,
  },
  {
    name: "Shahzeb Kamal",
    role: "Mathematician",
    linkedin: "https://www.linkedin.com/in/shahzeb-kamal-0a8145329/",
    image: "/sha.jpg",
    bio: "Sha is a mathematician with a background in theoretical and applied mathematics. He is CTO at CompliancePlay and a mathematician at Thoughtful Oasis. His work includes research on large language models, agents, and geometric representations of latent spaces, as well as building tooling for LLMs and agent-based systems. He maintains a broad interest in mathematical problems across disciplines."
  }
];

const LogoTopLeft = () => (
  <div className="absolute top-0 left-0 pt-6 md:pt-6 lg:pt-8 pl-4 md:pl-8 z-20 flex items-center gap-2">
    <a href="/" className="flex items-center gap-2">
      <img
        src="/assets/rhea/logo.png"
        alt="Thoughtful Oasis Logo"
        className="h-8 md:h-12 object-contain drop-shadow-lg"
        style={{ maxWidth: 50 }}
      />
      <span className="hidden sm:inline text-white text-lg md:text-xl lg:text-2xl font-bold">Thoughtful Oasis</span>
    </a>
  </div>
);

const LandingNavbar = () => (
  <nav className="flex bg-transparent transition-colors duration-700 flex-row absolute top-0 right-0 pt-6 md:pt-8 lg:pt-10 pr-4 md:pr-8 z-10 text-white justify-end items-center">
    <div className="flex flex-row gap-4 md:gap-8 text-base md:text-lg font-semibold">
      <a href="https://arxiv.org/abs/2505.14940" className="hover:text-pitchDeckTeal transition-colors duration-200">Research</a>
      <a href="https://iamrhea.com/nl" className="hover:text-pitchDeckTeal transition-colors duration-200">Try Rhea</a>
      <a href="/about-us" className="hover:text-pitchDeckTeal transition-colors duration-200">About Us</a>
    </div>
  </nav>
);

const mission = `Our mission is to build the next generation of search systems, build from the ground up to exploit the true power of LLMs.\nWe're building crucial AI architecture that turns any dataset into a interpretable and auditable search platform, based on ontology engineering and symbolic reasoning. Our goal is to eliminate the guesswork in information retrieval, even in the most complex and dynamic environments, whilst remaining transparant on what is retrieved.\nAs a remote-first startup with roots in Amsterdam and Enschede, we combine pragmatic engineering with research-driven problem solving. Our diverse, international team brings experience in AI, human-centered product design, and high-complexity environments, and we are committed to creating tools that empower users with clarity, trust, and speed.`;

// LinkedIn SVG icon
const LinkedInIcon = () => (
  <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" className="inline-block align-middle">
    <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11 19h-3v-9h3v9zm-1.5-10.28c-.97 0-1.75-.79-1.75-1.75s.78-1.75 1.75-1.75 1.75.79 1.75 1.75-.78 1.75-1.75 1.75zm13.5 10.28h-3v-4.5c0-1.08-.02-2.47-1.5-2.47-1.5 0-1.73 1.17-1.73 2.39v4.58h-3v-9h2.89v1.23h.04c.4-.75 1.38-1.54 2.84-1.54 3.04 0 3.6 2 3.6 4.59v4.72z"/>
  </svg>
);

// X (Twitter) SVG icon placeholder
const XIcon = () => (
  <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24" className="inline-block align-middle">
    <path d="M17.53 3H21l-7.19 8.19L22 21h-6.56l-5.25-6.32L3.47 21H0l7.81-8.89L2 3h6.56l4.73 5.7L17.53 3zm-2.13 15h2.13l-5.5-6.63-1.6 1.82L15.4 18zM6.47 5l5.5 6.63 1.6-1.82L8.6 6H6.47z"/>
  </svg>
);

const ProfileCard = ({ name, role, linkedin, image, bio }: typeof profiles[0]) => (
  <div className="bg-pitchDeckBlack border border-pitchDeckTeal rounded-xl p-6 flex flex-row items-start shadow-lg h-full backdrop-blur-md text-left gap-6">
    <div className="w-24 h-24 rounded-lg shadow-md flex-shrink-0 bg-pitchDeckGray flex items-center justify-center overflow-hidden">
      <img
        src={image}
        alt={name}
        className="object-cover object-top w-full h-full rounded-lg"
      />
    </div>
    <div className="flex flex-col justify-between h-full flex-1">
      <div>
        <div className="text-lg font-bold text-white mb-1">{name}</div>
        <div className="text-pitchDeckTeal font-semibold mb-2">{role}</div>
        <div className="text-white/90 text-sm mb-4">{bio}</div>
      </div>
      <div className="flex flex-row gap-4 items-center mt-2">
        <a
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="text-white hover:text-pitchDeckWhite"
          aria-label={`LinkedIn profile of ${name}`}
        >
          <LinkedInIcon />
        </a>
      </div>
    </div>
  </div>
);

const AboutUs = () => {
  return (
    <AuroraBackground className="h-[100vh] overflow-y-scroll w-full flex flex-col items-center bg-pitchDeckBlack justify-start relative px-2 pb-24">
      <LogoTopLeft />
      <LandingNavbar />
      <div className="pt-32 md:pt-44 w-full max-w-5xl mx-auto flex flex-col items-center">
        {/* Header and mission side-by-side */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-start mb-12 md:mb-16 px-4 md:px-0">
          {/* Header */}
          <div className="flex-1">
            <div className="text-[2rem] md:text-[4rem] lg:text-[4.5rem] font-extrabold text-white leading-none mb-2 text-left" style={{fontFamily: 'sans-serif'}}>Thoughtful Oasis</div>
            <div className="text-xl md:text-4xl lg:text-4xl text-pitchDeckTeal text-left pl-2">Team</div>
          </div>
          {/* Mission Text Box */}
          <div className="flex-1 bg-pitchDeckGray/20 border border-pitchDeckTeal rounded-xl p-6 md:p-8 shadow-lg text-left text-white/90 text-sm md:text-base font-mono">
            {mission.split('\n').map((line, idx) => (
              <p key={idx} className="mb-4 last:mb-0">{line}</p>
            ))}
          </div>
        </div>
        {/* Team grid */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 justify-center items-start px-4 md:px-0">
          {profiles.map((profile) => (
            <ProfileCard key={profile.name} {...profile} />
          ))}
        </div>
      </div>
    </AuroraBackground>
  );
};

export default AboutUs;
